import React, { useContext, useEffect } from 'react';

import { TrackEventContext } from 'common/containers/EventContainer';
import JaneTechnologiesLandingHelmet from 'common/helmets/JaneTechnologiesLandingHelmet';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import UppercaseHeader from 'common/UppercaseHeader';
import LandingCaseStudyHeadingText from 'common/www/LandingCaseStudyHeadingText';
import JaneTechnologiesWordmark from 'img/landing/janetechnologies-wordmark.webp';

import 'css/components/www/_LandingCustomersPage.scss';

const JaneTechnologiesLanding = () => {
  const trackEvent = useContext(TrackEventContext);

  useEffect(() => {
    trackEvent('Viewed Jane Technologies Case Study');
  }, [trackEvent]);

  return (
    <div className="landingCustomersPage">
      <JaneTechnologiesLandingHelmet />
      <div className="fullWidthContainer">
        <div className="header">
          <LandingCaseStudyHeadingText title="How Jane Technologies streamlined user feedback and boosted company culture" />
          <div className="headerVideo">
            <iframe
              width="560"
              height="315"
              src="https://www.youtube.com/embed/8GzZkqgdLkM"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen></iframe>
          </div>
        </div>
      </div>
      <div className="mainContent">
        <div className="mainContentText">
          <div className="left">
            <p>
              Jane Technologies is a comprehensive tool for the cannabis community. Today, over
              2,500 dispensaries and brands use Jane to create website menus, review data, and
              manage their businesses&nbsp;online.
            </p>
            <p>
              Rachel Schaaf is Jane’s senior director of partner success and technical support. That
              means she and her team are responsible for supporting millions of end-users and
              thousands of retail partners. Not an easy&nbsp;task!
            </p>
            <h2 className="sectionTitle">Challenge</h2>
            <p>
              As Rachel onboards new partners and clients, she hears a lot of ideas for improvement.
              At first, she’d track those requests in ClickUp. Since the engineers at Jane use
              ClickUp, it made sense…until it&nbsp;didn’t.
            </p>
            <p className="quote">
              &ldquo;It became a black hole of lists with no context. There was no easy and quick
              way of seeing which issue is the most urgent or&nbsp;important.&rdquo;
            </p>
            <p>
              What’s more, Rachel had to backtrack through each request to figure out who requested
              what. Jane Technologies works with many partners, so keeping track of every partner’s
              requests was too&nbsp;challenging.
            </p>
            <p>
              Rachel was spending hours manually tracking each feature request, exporting them to
              Excel, and trying to match them to&nbsp;partners.
            </p>
            <h2 className="sectionTitle">Solution</h2>
            <p>
              When a new product manager came on board, the team identified their number one goal:
              improve communication across the&nbsp;organization.
            </p>
            <p>
              The search for a product management tool began. After considering a few alternatives,
              Jane selected Canny. In particular, the team really needed a tool that integrated with
              their existing tech&nbsp;stack.
            </p>
            <p>
              The fact that updates in Canny automatically sync with ClickUp and Salesforce is a
              huge help for Jane for&nbsp;Technologies.
            </p>
            <p className="quote">
              &ldquo;When our sales reps are talking to a prospect, they often hear a request for a
              very specific feature. Now, they can quickly log that in Canny and mention how
              important it is. For example, if no one in the industry does that, it presents a huge
              opportunity for&nbsp;us.&rdquo;
            </p>
            <p>
              Rachel loves that she doesn’t have to import all of Jane’s older requests manually. It
              was a huge time&nbsp;saver!
            </p>
            <p>On an ongoing basis, Canny saves Rachel hours every&nbsp;week.</p>
            <p>
              Rachel adds that Canny brings visibility to the entire organization. Before, the sales
              team might not have known about certain new features, but now everyone’s on the same
              page. Similarly, the product team might not have known what features sales needed to
              close&nbsp;deals.
            </p>
            <p>Now, everyone is on the same&nbsp;page.</p>
            <h2 className="sectionTitle">Results</h2>
            <p>
              Overall, the transition to Canny was smooth and seamless, according to&nbsp;Rachel.
            </p>
            <p className="quote">
              &ldquo;Now I can just invite anyone from the team to submit a request in Canny. They
              can vote on features on behalf of their&nbsp;partners.&rdquo;
            </p>
            <p>
              On top of that, Rachel believes that they achieved their goal of improving team
              communication thanks to&nbsp;Canny.
            </p>
            <p className="quote">
              &ldquo;We’re much more efficient with our feature requests now. Canny’s consolidating
              ideas and making our requests as straightforward as&nbsp;possible.&rdquo;
            </p>
            <p>
              These positive changes also helped the culture at Jane. Rachel noted that Jane has
              always been an open and transparent organization. So, getting everyone on the same
              page with feature prioritization has been instrumental in supporting that. It also
              helps to set reasonable expectations around timelines and&nbsp;priorities.
            </p>
            <p>
              Canny brought some additional benefits to Rachel’s team that she
              didn’t&nbsp;anticipate.
            </p>
            <p className="quote">
              &ldquo;I didn’t expect to use the roadmap as much as I do. Now, it’s a big part of our
              planning conversations. I can quickly see and follow up on the projects that are
              past-due for&nbsp;example.&rdquo;
            </p>
            <p>
              Rachel mentions that their customers have noted how Jane’s response time has
              improved&nbsp;too.
            </p>
            <p>To teams that are contemplating getting Canny, Rachel&nbsp;recommends:</p>
            <p className="quote">
              &ldquo;Show it to your teams. As soon as they start using it, they’ll see how easy it
              is and how much more streamlined your life could&nbsp;be.&rdquo;
            </p>
            <p>
              We couldn’t agree more!{' '}
              <a target="_blank" href="https://canny.io/register" rel="noopener">
                Give Canny a free try here!
              </a>
            </p>
          </div>
          <div className="right">
            <LazyLoadedImage
              alt="JaneTechnologies Wordmark"
              className="janeTechnologiesWordmark"
              src={JaneTechnologiesWordmark}
            />
            Jane Technologies is a comprehensive tool for the cannabis&nbsp;community.
            <a href="https://www.iheartjane.com/" rel="noopener" target="_blank">
              iheartjane.com
            </a>
            <div className="stat">
              <UppercaseHeader>Employees</UppercaseHeader>
              <p>200</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Using Canny since</UppercaseHeader>
              <p>2019</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Votes on Canny</UppercaseHeader>
              <p>3,000+</p>
            </div>
          </div>
        </div>
        <LandingFooterCTA style="solid" expanded={true} />
      </div>
    </div>
  );
};

export default JaneTechnologiesLanding;
